import { useKeycloak } from '@react-keycloak/web';
import { getUserInfo } from '../requests/user.request';
import { IUser } from '../types/user.types';
import {
  createContext, useContext, useEffect, useState, ReactNode
} from 'react';
import { getModules, updateModules } from '../requests/super-admin.request';
import { useKato } from './kato.context';
import { KatoType } from '../types/kato.types';
import { toast } from 'react-toastify';

export interface IModules {
  [KatoType.OBLAST]: string[];
  [KatoType.VILLAGE]: string[];
}
interface UserContextType {
  user: IUser | null;
  setUser: (user: IUser | null) => void;
  loading: boolean;
  error: string | null;
  modules: string[];
  handleUpdateModules: (modules: IModules) => void;
  isModulePresent: (module: string) => boolean;
  moduleData: IModules;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { keycloak, initialized } = useKeycloak();
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modules, setModules] = useState<string[]>([]);
  const [moduleData, setModuleData] = useState<IModules>({ [KatoType.OBLAST]: [], [KatoType.VILLAGE]: [] });
  const { currentLevel } = useKato();

  const handleUpdateModules = (modules: IModules) => {
    updateModules(modules)
      .then(() => {
        getModules()
          .then(data => {
            setModules(data[currentLevel]);
            setModuleData(data);
          });
        toast.success('Настройки успешно сохранены');
      })
      .catch(() => toast.error('Ошибка при сохранении настроек'));
  }

  const isModulePresent = (module: string) => {
    return modules.includes(module);
  }

  useEffect(() => {
    if (!initialized || !keycloak?.authenticated || !keycloak.token) return;

    getUserInfo()
      .then(setUser)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));

    getModules()
      .then(data => {
        const res = currentLevel === KatoType.VILLAGE ? (data?.[KatoType.VILLAGE] || []) : (data?.[KatoType.OBLAST] || []);
        setModules(res);
        setModuleData(data);
      })

  }, [initialized, keycloak?.authenticated, keycloak?.token, currentLevel]);

  return (
    <UserContext.Provider value={{ user, setUser, loading, error, modules, handleUpdateModules, isModulePresent, moduleData }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
